/* De lijst van Businesscases (/crm/businesscases): ophalen, de cijfers, tabs, zoeken,
 * sorteren en de filter in de URL. Zelfde opzet als de projectlijst (ADR 0023).
 *
 * De cijfers worden hier uitgerekend (calculateBusinesscase), niet uit de opgeslagen
 * kolommen gelezen: die werden alleen bijgewerkt als iemand de case opende, en een
 * terugverdientijd met decimalen liet dat schrijven mislukken (payback_period is een
 * integer). Op 29-09-2026 had JWM zo 303.434 euro opbrengst en een opgeslagen
 * terugverdientijd van niets.
 *
 * Relatieve imports (geen @/), zodat de toetsen dit bestand zonder bundel lezen. */

import { calculateBusinesscase } from '../../../lib/businesscaseCalculations';
import { lijstGeheugen, vindt } from '../../relaties/data/geheugen';

export interface BcClient {
  from(tabel: string): any;
}

export const BC_KOLOMMEN =
  'id, project_id, name, notes, version, is_archived, archived_at, roi_calculation_mode, battery_purchase_cost, installation_cost, software_setup_cost, annual_subscription, other_costs, eia_enabled, flex_e_enabled, flex_e_value, other_subsidy, tax_rate, created_at, updated_at, created_by';
export const OPBRENGST_KOLOMMEN =
  'businesscase_id, year, grid_connection_savings, grid_purchase_cost_savings, dayahead_incomes, hbe_incomes, energy_tax_savings, endex_cost_savings, imbalance_income, imbalance_discount, ems_costs, total_opex, generator_cost_savings, extra_sde, total_solar_revenues';

export interface BcProject {
  id: string;
  nummer: string | null;
  naam: string;
  organisatie: string | null;
  verwijderd: boolean;
}

export interface Cijfers {
  investering: number;
  nettoInvestering: number;
  opbrengst: number;
  kasstroom: number;
  terugverdientijd: number | null;
  roiJaar1: number | null;
  jaren: number;
  eersteJaar: number | null;
  laatsteJaar: number | null;
}

export interface LijstBusinesscase {
  id: string;
  naam: string;
  versie: number;
  gearchiveerd: boolean;
  modus: 'bruto' | 'netto';
  project_id: string | null;
  project: BcProject | null;
  cijfers: Cijfers;
  /** Geen kosten én geen opbrengsten: zo maakte de oude knop "Businesscase" hem aan. */
  leeg: boolean;
  /** Wel iets, maar kosten of opbrengsten ontbreken nog. */
  onvolledig: boolean;
  aangemaakt: string;
  bijgewerkt: string | null;
}

const num = (v: unknown) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? 0 : Number(v));

/** De cijfers van één case uit de invoer: dezelfde berekening als de casepagina. */
export function cijfersVoor(bc: Record<string, any>, opbrengsten: Record<string, any>[], extra: { amount: unknown }[]): Cijfers {
  const r = calculateBusinesscase({
    costs: {
      batteryPurchaseCost: num(bc.battery_purchase_cost),
      installationCost: num(bc.installation_cost),
      softwareSetupCost: num(bc.software_setup_cost),
      annualSubscription: num(bc.annual_subscription),
      otherCosts: num(bc.other_costs),
    },
    subsidies: {
      eiaEnabled: Boolean(bc.eia_enabled),
      flexEEnabled: Boolean(bc.flex_e_enabled),
      flexEValue: num(bc.flex_e_value),
      otherSubsidy: num(bc.other_subsidy),
      taxRate: num(bc.tax_rate),
    },
    revenues: opbrengsten as any,
    extraCosts: extra.map((e) => ({ amount: num(e.amount) })) as any,
    roiMode: bc.roi_calculation_mode === 'netto' ? 'netto' : 'bruto',
  });
  const jaren = opbrengsten.map((o) => Number(o.year)).filter(Number.isFinite).sort((a, b) => a - b);
  return {
    investering: r.totalInvestment,
    nettoInvestering: r.netInvestment,
    opbrengst: r.totalRevenues,
    kasstroom: r.totalCashflow,
    /* zonder investering valt er niets terug te verdienen (calculateBusinesscase zegt dan 1 jaar) */
    terugverdientijd: r.totalInvestment > 0 ? r.paybackPeriod : null,
    roiJaar1: r.roiYear1,
    jaren: jaren.length,
    eersteJaar: jaren[0] ?? null,
    laatsteJaar: jaren.at(-1) ?? null,
  };
}

export async function haalBusinesscases(client: BcClient): Promise<LijstBusinesscase[]> {
  const bcs = await client.from('businesscases').select(BC_KOLOMMEN);
  if (bcs.error) throw bcs.error;
  const rijen: any[] = bcs.data ?? [];
  const ids = rijen.map((b) => b.id);
  const projectIds = [...new Set(rijen.map((b) => b.project_id).filter(Boolean))];
  const leeg = Promise.resolve({ data: [], error: null });
  const [opbrengsten, extra, projecten] = await Promise.all([
    ids.length ? client.from('businesscase_revenues').select(OPBRENGST_KOLOMMEN).in('businesscase_id', ids) : leeg,
    ids.length ? client.from('businesscase_extra_costs').select('businesscase_id, amount').in('businesscase_id', ids) : leeg,
    projectIds.length ? client.from('projects').select('id, name, project_number, org_id, deleted_at').in('id', projectIds) : leeg,
  ]);
  for (const a of [opbrengsten, extra, projecten]) if (a.error) throw a.error;
  const orgIds = [...new Set((projecten.data ?? []).map((p: any) => p.org_id).filter(Boolean))];
  const orgs = new Map<string, string>();
  if (orgIds.length) {
    const o = await client.from('organizations').select('id, name').in('id', orgIds);
    if (o.error) throw o.error;
    for (const r of o.data ?? []) orgs.set(r.id, r.name);
  }
  const perCase = <T,>(lijst: any[]) => {
    const m = new Map<string, T[]>();
    for (const r of lijst ?? []) m.set(r.businesscase_id, [...(m.get(r.businesscase_id) ?? []), r]);
    return m;
  };
  const opbrengstPer = perCase<any>(opbrengsten.data);
  const extraPer = perCase<any>(extra.data);
  const projectMap = new Map<string, BcProject>(
    (projecten.data ?? []).map((p: any) => [p.id, {
      id: p.id,
      nummer: p.project_number || null,
      naam: p.name?.trim() || '(zonder naam)',
      organisatie: p.org_id ? orgs.get(p.org_id) ?? null : null,
      verwijderd: Boolean(p.deleted_at),
    }]),
  );

  return rijen.map((b) => {
    const opb = opbrengstPer.get(b.id) ?? [];
    const ext = extraPer.get(b.id) ?? [];
    const cijfers = cijfersVoor(b, opb, ext);
    const heeftKosten = cijfers.investering > 0;
    const heeftOpbrengst = cijfers.opbrengst > 0;
    return {
      id: String(b.id),
      naam: b.name?.trim() || '(zonder naam)',
      versie: Number(b.version) || 1,
      gearchiveerd: Boolean(b.is_archived),
      modus: b.roi_calculation_mode === 'netto' ? 'netto' : 'bruto',
      project_id: b.project_id ?? null,
      project: b.project_id ? projectMap.get(b.project_id) ?? { id: b.project_id, nummer: null, naam: '(project niet gevonden)', organisatie: null, verwijderd: true } : null,
      cijfers,
      leeg: !heeftKosten && !heeftOpbrengst,
      onvolledig: heeftKosten !== heeftOpbrengst,
      aangemaakt: b.created_at,
      bijgewerkt: b.updated_at ?? null,
    };
  });
}

/* ── tabs ─────────────────────────────────────────────────────────── */

export const TABS = ['actief', 'onvolledig', 'gearchiveerd', 'alle'] as const;
export type Tab = (typeof TABS)[number];

export const TAB_LABEL: Record<Tab, string> = {
  actief: 'Actief',
  onvolledig: 'Leeg of onvolledig',
  gearchiveerd: 'Gearchiveerd',
  alle: 'Alle',
};

export function inTab(b: LijstBusinesscase, tab: Tab): boolean {
  switch (tab) {
    case 'actief': return !b.gearchiveerd;
    case 'onvolledig': return !b.gearchiveerd && (b.leeg || b.onvolledig);
    case 'gearchiveerd': return b.gearchiveerd;
    case 'alle': return true;
  }
}

/* ── zoeken en sorteren ───────────────────────────────────────────── */

export type Sortering = 'project' | 'naam' | 'investering' | 'terugverdientijd' | 'bijgewerkt';
export const SORTERINGEN: Sortering[] = ['project', 'naam', 'investering', 'terugverdientijd', 'bijgewerkt'];

export interface LijstFilter {
  zoek: string;
  tab: Tab;
  sortering: Sortering;
  oplopend: boolean;
}

export const STANDAARD_FILTER: LijstFilter = { zoek: '', tab: 'actief', sortering: 'project', oplopend: true };

/** Bij een nieuwe sortering: tekst en terugverdientijd oplopend, bedragen en datums aflopend. */
export const beginRichting = (s: Sortering) => s === 'project' || s === 'naam' || s === 'terugverdientijd';

export function passendBijZoek(b: LijstBusinesscase, zoek: string): boolean {
  return vindt(zoek, [b.naam, b.project?.nummer, b.project?.naam, b.project?.organisatie]);
}

const nummerVan = (s: string | null | undefined) => {
  const m = /(\d+)/.exec(s ?? '');
  return m ? Number(m[1]) : Number.MAX_SAFE_INTEGER;
};
const tekst = (a: string, b: string) => a.localeCompare(b, 'nl', { sensitivity: 'base', numeric: true });

export function filterEnSorteer(lijst: LijstBusinesscase[], f: LijstFilter): LijstBusinesscase[] {
  const uit = lijst.filter((b) => inTab(b, f.tab) && passendBijZoek(b, f.zoek));
  const r = f.oplopend ? 1 : -1;
  const opNaam = (a: LijstBusinesscase, b: LijstBusinesscase) => tekst(a.naam, b.naam);
  /* null (niet te berekenen) staat altijd achteraan */
  const getal = (x: number | null, y: number | null) => (x === null && y === null ? 0 : x === null ? 1 : y === null ? -1 : r * (x - y));
  return uit.sort((a, b) => {
    switch (f.sortering) {
      case 'naam': return r * opNaam(a, b);
      case 'investering': {
        /* zonder kosten geen investering: achteraan */
        const inv = (x: LijstBusinesscase) => (x.cijfers.investering > 0 ? x.cijfers.nettoInvestering : null);
        return getal(inv(a), inv(b)) || opNaam(a, b);
      }
      case 'terugverdientijd': return getal(a.cijfers.terugverdientijd, b.cijfers.terugverdientijd) || opNaam(a, b);
      case 'bijgewerkt': {
        const x = a.bijgewerkt ?? a.aangemaakt;
        const y = b.bijgewerkt ?? b.aangemaakt;
        return (x === y ? 0 : x < y ? -r : r) || opNaam(a, b);
      }
      default:
        return r * (nummerVan(a.project?.nummer) - nummerVan(b.project?.nummer)) || opNaam(a, b);
    }
  });
}

export interface TabTelling {
  tab: Tab;
  aantal: number;
}

export function tellingen(lijst: LijstBusinesscase[], zoek: string): TabTelling[] {
  const basis = lijst.filter((b) => passendBijZoek(b, zoek));
  return TABS.map((tab) => ({ tab, aantal: basis.filter((b) => inTab(b, tab)).length }));
}

/* ── filter in de URL ─────────────────────────────────────────────── */

export type Weergave = 'lijst' | 'vergelijken';

export function filterUitUrl(p: URLSearchParams): LijstFilter {
  const tab = p.get('tab');
  const sort = p.get('sort') as Sortering;
  const sortering = SORTERINGEN.includes(sort) ? sort : STANDAARD_FILTER.sortering;
  const dir = p.get('dir');
  return {
    zoek: p.get('q') ?? '',
    tab: TABS.includes(tab as Tab) ? (tab as Tab) : STANDAARD_FILTER.tab,
    sortering,
    oplopend: dir === 'asc' ? true : dir === 'desc' ? false : beginRichting(sortering),
  };
}

export function filterNaarUrl(f: LijstFilter): Record<string, string> {
  const uit: Record<string, string> = {};
  if (f.zoek) uit.q = f.zoek;
  if (f.tab !== STANDAARD_FILTER.tab) uit.tab = f.tab;
  if (f.sortering !== STANDAARD_FILTER.sortering) uit.sort = f.sortering;
  if (f.oplopend !== beginRichting(f.sortering)) uit.dir = f.oplopend ? 'asc' : 'desc';
  return uit;
}

export const businesscaseLijst = lijstGeheugen('businesscases', '/crm/businesscases');
